import * as d3 from 'd3';

class ScatterplotView extends HTMLElement {
  // Save dimensions between renderings
  static dimensions = {
    width: 800,
    height: 600
  };

  connectedCallback() {

    // Listen for window resize to adjust size
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Listen for HTMX updates
    document.body.addEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
    
    // Initial rendering
    this.renderScatterplot();
  }
  
  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize.bind(this));
    document.body.removeEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
  }
  
  handleResize() {
    if (document.body.contains(this)) {
      const container = this.querySelector('#scatterplot-container');
      if (container) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 10 && rect.height > 10) {
          // Update our static dimensions when the size changes
          ScatterplotView.dimensions = {
            width: rect.width,
            height: rect.height
          };
        }
      }
      this.renderScatterplot();
    }
  }
  
  handleHtmxUpdate() {
    if (document.body.contains(this)) {
      this.renderScatterplot();
    }
  }
  
  renderScatterplot() {
    const dataEl = this.querySelector('#scatterplot-data');
    if (!dataEl) return;
    
    const data = JSON.parse(dataEl.getAttribute('x-data'));
    this.createScatterplot(data);
  }

  createScatterplot(data) {
    const container = this.querySelector('#scatterplot-container');
    if (!container) return;
    
    // Use container dimensions if available, otherwise the saved ones
    const rect = container.getBoundingClientRect();
    const width = (rect.width > 10) ? rect.width : ScatterplotView.dimensions.width;
    const height = (rect.height > 10) ? rect.height : ScatterplotView.dimensions.height;
    
    // If it's a "real" size, save it for future use
    if (width > 10 && height > 10) {
      ScatterplotView.dimensions = { width, height };
    }
    // Clear previous SVG
    d3.select(container).selectAll("svg").remove();
    d3.select(container).selectAll(".tooltip").remove();
    let points = [];
    if (data.children) {
      Object.values(data.children).forEach(arr => {
        arr.forEach(obj => {
          points.push({
            ...obj,
            y: typeof obj.x === "string" ? parseFloat(obj.x) : obj.x,
            x: typeof obj.y === "string" ? parseFloat(obj.y) : obj.y,
            time: typeof obj.year === "string" ? new Date(obj.year) : obj.year,
          });
        });
      });
    }

    points.sort((a, b) => +a.time - +b.time);

  
    console.log('Scatterplot width:', width);
 
    console.log('Scatterplot height:', height);
    const margin = { top: 20, right: 30, bottom: 40, left: 60 };



    const svg = d3.select(container)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    const x = d3.scaleLinear()
      .domain(d3.extent(points, d => d.x)).nice()
      .range([margin.left, width - margin.right]);

    const y = d3.scaleLinear()
      .domain(d3.extent(points, d => d.y)).nice()
      .range([height - margin.bottom, margin.top]);

    const xAxis = g => g
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(width / 80))
      .call(g => g.select(".domain").remove());

    const yAxis = g => g
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(null))
      .call(g => g.select(".domain").remove());

    const gMain = svg.append("g").attr("class", "main-group");
    const gridGroup = gMain.append("g").attr("class", "grid");

    function drawGrid(zx = x, zy = y) {
      const xTicks = zx.ticks(width / 80);
      const xLines = gridGroup.selectAll(".x-grid").data(xTicks, d => d);
      xLines.join(
        enter => enter.append("line").attr("class", "x-grid")
          .attr("stroke", "#ccc").attr("stroke-width", 1).attr("stroke-opacity", 0.3),
        update => update,
        exit => exit.remove()
      ).attr("x1", d => zx(d)).attr("x2", d => zx(d))
       .attr("y1", margin.top).attr("y2", height - margin.bottom);

      const yTicks = zy.ticks(height / 50);
      const yLines = gridGroup.selectAll(".y-grid").data(yTicks, d => d);
      yLines.join(
        enter => enter.append("line").attr("class", "y-grid")
          .attr("stroke", "#ccc").attr("stroke-width", 1).attr("stroke-opacity", 0.3),
        update => update,
        exit => exit.remove()
      ).attr("y1", d => zy(d)).attr("y2", d => zy(d))
       .attr("x1", margin.left).attr("x2", width - margin.right);
    }

    drawGrid();
    gMain.append("g").attr("class", "x-axis").call(xAxis);
    gMain.append("g").attr("class", "y-axis").call(yAxis);

    const line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.x))
      .y(d => y(d.y));

    const path = gMain.append("path")
      .datum(points)
      .attr("fill", "none")
      .attr("stroke", "#e77b3c")
      .attr("stroke-width", 2.5)
      .attr("d", line);

    const circles = gMain.selectAll("circle")
      .data(points)
      .join("circle")
      .attr("cx", d => x(d.x))
      .attr("cy", d => y(d.y))
      .attr("r", 4)
      .attr("fill", "white")
      .attr("stroke", "#3b291e")
      .attr("stroke-width", 2.5);

    const tooltip = d3.select(container)
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
      .style("font-size", "12px");

    circles
      .on("mouseover", function(event, d) {
        d3.select(this).attr("r", 8);
        tooltip.style("visibility", "visible").html(`${d.label || ""}`);
      })
      .on("mousemove", function(event) {
        tooltip
          .style("top", (event.pageY - 140) + "px")
          .style("left", (event.pageX - 140) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", 4);
        tooltip.style("visibility", "hidden");
      });

    const totalLength = path.node().getTotalLength();
    path
      .attr("stroke-dasharray", `${totalLength},${totalLength}`)
      .attr("stroke-dashoffset", totalLength)
      .transition()
      .duration(10000)
      .ease(d3.easeLinear)
      .attr("stroke-dashoffset", 0);

    svg.call(
      d3.zoom()
        .scaleExtent([1, 40])
        .extent([[margin.left, margin.top], [width - margin.right, height - margin.bottom]])
        .on("zoom", (event) => {
          const t = event.transform;
          const zx = t.rescaleX(x);
          const zy = t.rescaleY(y);

          drawGrid(zx, zy);
          gMain.select(".x-axis").call(d3.axisBottom(zx).ticks(width / 80));
          gMain.select(".y-axis").call(d3.axisLeft(zy).ticks(null));
          path.attr("d", line.x(d => zx(d.x)).y(d => zy(d.y)));
          circles.attr("cx", d => zx(d.x)).attr("cy", d => zy(d.y));
        })
    );

    container.addEventListener('wheel', e => e.preventDefault(), { passive: false });
  }
}

if (!customElements.get("scatterplot-view")) {
  customElements.define("scatterplot-view", ScatterplotView);
}
